import {Component, OnInit} from '@angular/core';
import {HttpResponse} from '@angular/common/http';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {AddpRequestDetailsResponse, OrderFormService} from '../../services/order-form.service';
import {
  MarketDataLedgerCommercialPop,
  MarketDataLedgerCrossConnect,
  MarketDataLedgerEntry,
  MarketDataLedgerOrderFormItem
} from '../../models/market-data-ledger';
import {Company} from '../../models/company';
import {CompanySummary} from '../../models/company-summary';
import {UserLoggedRolesServiceService} from '../../authenticationModule/services/user-logged-roles-service.service';
import {CompanySelectionService} from '../../services/company-selection.service';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {environment} from '../../../environments/environment';

@Component({
  selector: 'app-market-data-ledgers',
  templateUrl: './market-data-ledgers.component.html',
  styleUrls: ['./market-data-ledgers.component.scss']
})
export class MarketDataLedgersComponent implements OnInit {
  private readonly mdmItemUrl = this.buildMdmItemUrlBase();
  ledgers: MarketDataLedgerEntry[] = [];
  selectedLedger: MarketDataLedgerEntry | null = null;
  ledgerOptions: {label: string; value: string}[] = [];
  selectedLedgerId: string | null = null;
  isMckayUser = false;
  companies: CompanySummary[] = [];
  companyOptions: SelectItem[] = [];
  selectedCompany: (Company | CompanySummary) | null = null;
  selectedCompanyId: number | null = null;
  loadingCompanies = false;
  companyError: string | null = null;
  loading = true;
  error = false;
  downloading = false;
  downloadError = false;
  cancellingRequestId: string | null = null;
  addpDetailsVisible = false;
  loadingAddpDetails = false;
  addpDetailsError: string | null = null;
  selectedAddpDetails: AddpRequestDetailsResponse | null = null;

  constructor(private authenticationService: AuthenticationService,
              private orderFormService: OrderFormService,
              private userRolesService: UserLoggedRolesServiceService,
              private companySelectionService: CompanySelectionService,
              private confirmationService: ConfirmationService,
              private utils: UtilsService) {
  }

  ngOnInit(): void {
    const currentUser = this.authenticationService.getUserCurrent();
    this.isMckayUser = currentUser?.permissions.isMckayUser ?? false;

    if (this.isMckayUser) {
      this.loadCompanies();
    } else {
      this.selectedCompany = currentUser?.company ?? null;
      this.selectedCompanyId = this.selectedCompany?.id ?? null;
      this.loadLedgers();
    }
  }

  onCompanyChange(companyId: number | null): void {
    if (companyId == null) {
      this.selectedCompany = null;
      this.selectedCompanyId = null;
      this.resetLedgerState();
      return;
    }
    const company = this.companies.find(c => c.id === companyId) ?? null;
    this.selectedCompany = company;
    this.selectedCompanyId = company?.id ?? null;
    if (company) {
      this.loadLedgers();
    } else {
      this.resetLedgerState();
    }
  }

  onLedgerChange(value: string): void {
    this.selectedLedgerId = value;
    this.selectedLedger = this.ledgers.find(ledger => ledger.orderForm.identificationNumber === value) ?? null;
  }

  get currentCompany(): Company | undefined {
    return this.authenticationService.getUserCurrent()?.company;
  }

  get canSeeMonthlyFee(): boolean {
    const user = this.authenticationService.getUserCurrent();
    if (!user) {
      return false;
    }
    return this.userRolesService.userHasOneOfThoseRoles(user, ['ROLE_CLIENT_SUPER', 'ROLE_CLIENT_ADMIN', 'ROLE_CLIENT_FINANCE', 'ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN']);
  }

  formatCrossConnects(connections: MarketDataLedgerCrossConnect[]): string {
    if (!connections || connections.length === 0) {
      return '';
    }
    const formatted = connections.map(connection => {
      if (connection.technicalPop) {
        return `${connection.crossConnectId} (${connection.technicalPop})`;
      }
      return connection.crossConnectId || '';
    }).filter(value => !!value);
    if (formatted.length === 0) {
      return '';
    }
    return formatted.join(', ');
  }

  hostedByThirdPartyLabel(item: MarketDataLedgerOrderFormItem): string {
    if (item.hostedByThirdParty === true) {
      return 'Y';
    }
    if (item.hostedByThirdParty === false) {
      return 'N';
    }
    return '—';
  }

  trackItem(index: number, item: MarketDataLedgerOrderFormItem): string {
    return item.uuid ?? `${index}`;
  }

  trackCommercialPop(index: number, pop: MarketDataLedgerCommercialPop): string {
    return pop.displayId ?? `${index}`;
  }

  downloadSelectedLedger(): void {
    const ledgerId = this.selectedLedger?.orderForm?.id;
    if (!ledgerId || this.downloading) {
      return;
    }
    this.downloading = true;
    this.downloadError = false;
    this.orderFormService.downloadLedgerOrderFormPdf(ledgerId, this.selectedCompanyId ?? undefined).subscribe({
      next: response => {
        const downloadName = this.extractFileName(response) || this.buildLedgerFileName();
        if (response.body) {
          this.triggerDownload(response.body, downloadName);
        }
        this.downloading = false;
      },
      error: () => {
        this.downloading = false;
        this.downloadError = true;
      }
    });
  }

  private extractFileName(response: HttpResponse<Blob>): string | null {
    const disposition = response.headers.get('content-disposition');
    if (disposition) {
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      if (match && match[1]) {
        return match[1];
      }
    }
    const fallback = response.headers.get('link');
    return fallback || null;
  }

  private buildLedgerFileName(): string {
    const orderRef = this.selectedLedger?.orderForm?.identificationNumber || 'ledger';
    return `${orderRef}.pdf`;
  }

  private enrichLedgerItem(item: MarketDataLedgerOrderFormItem): MarketDataLedgerOrderFormItem {
    const originalService = (item as any).original_service ?? item.originalService ?? null;
    const displayService = originalService ?? item.service ?? null;
    return {
      ...item,
      originalService,
      displayService,
      displayServiceLabel: this.formatServiceLabel(displayService),
      crossConnects: item.crossConnects ?? [],
      monthlyFee: typeof item.monthlyFee === 'number' ? item.monthlyFee : null,
      isPendingAddp: item.isPendingAddp ?? false,
      addpRequestId: item.addpRequestId ?? null,
      cancellationRequestId: item.cancellationRequestId ?? null,
      cancellationStatus: item.cancellationStatus ?? null,
      pendingRequestStatus: item.pendingRequestStatus ?? null
    };
  }

  private prepareCommercialPops(pops: MarketDataLedgerCommercialPop[]): MarketDataLedgerCommercialPop[] {
    return [...pops]
      .sort((a, b) => (a.displayId || '').localeCompare(b.displayId || ''))
      .map(pop => ({
        ...pop,
        address: pop.address || this.formatCommercialPopAddress(pop)
      }));
  }

  private formatCommercialPopAddress(pop: MarketDataLedgerCommercialPop): string | null {
    const segments: string[] = [];
    const street = (pop.street || '').trim();
    if (street) {
      segments.push(street);
    }
    const city = (pop.city || '').trim();
    if (city) {
      segments.push(city);
    }
    const statePostal: string[] = [];
    const state = (pop.state || '').trim();
    if (state) {
      statePostal.push(state);
    }
    const postal = (pop.postalCode || '').trim();
    if (postal) {
      statePostal.push(postal);
    }
    if (statePostal.length) {
      segments.push(statePostal.join(' '));
    }
    return segments.length ? segments.join(', ') : null;
  }

  private formatServiceLabel(service?: string | null): string | null {
    if (!service) {
      return null;
    }
    switch (service) {
      case 'qed':
        return 'Snapshot';
      case 'qrd':
        return 'Raw Data';
      case 'qfd':
        return 'Fast Data';
      default:
        return null;
    }
  }

  formatMonthlyFee(item: MarketDataLedgerOrderFormItem): string {
    const amount = item.monthlyFee;
    if (typeof amount === 'number' && !Number.isNaN(amount)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    }
    return '$1,080';
  }

  private sortLedgerItems(items: MarketDataLedgerOrderFormItem[]): MarketDataLedgerOrderFormItem[] {
    // Sorts items by commencement date, keeping entries without a date at the bottom.
    const parseTime = (value?: string): number | null => {
      if (!value) {
        return null;
      }
      const timestamp = new Date(value).getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    };
    return [...items].sort((a, b) => {
      const aTime = parseTime(a.serviceStartDate);
      const bTime = parseTime(b.serviceStartDate);
      if (aTime === null && bTime === null) {
        return 0;
      }
      if (aTime === null) {
        return 1;
      }
      if (bTime === null) {
        return -1;
      }
      return aTime - bTime;
    });
  }

  hasCrossConnectIds(connections: MarketDataLedgerCrossConnect[]): boolean {
    return (connections ?? []).some(connection => !!connection.crossConnectId);
  }

  buildPendingTooltip(item: MarketDataLedgerOrderFormItem): string | null {
    const text = this.getAddpRequestText(item);
    return text || (item.isPendingAddp ? 'ADDP request pending' : null);
  }

  getAddpRequestText(item: MarketDataLedgerOrderFormItem): string | null {
    const createdAt = this.parseDate(item.createdAt);
    if (!createdAt) {
      return null;
    }
    return `ADDP requested on ${createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  private parseDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private normalizeDate(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  private triggerDownload(content: Blob, filename: string): void {
    const blob = new Blob([content], {type: 'application/pdf'});
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  openCancelConfirmation(item: MarketDataLedgerOrderFormItem): void {
    if (!this.canCancel(item)) {
      return;
    }
    const pendingAddp = item.isPendingAddp === true;
    this.confirmationService.confirm({
      header: pendingAddp ? 'Cancel ADDP request' : 'Cancel ADDP',
      message: pendingAddp
        ? 'Are you sure you want to cancel this ADDP request?'
        : 'Are you sure you want to cancel this ADDP?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: pendingAddp ? 'Cancel request' : 'Cancel ADDP',
      rejectLabel: pendingAddp ? 'Keep request' : 'Keep ADDP',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.cancelLedgerEntry(item)
    });
  }

  canCancel(item: MarketDataLedgerOrderFormItem): boolean {
    if (this.hasPendingCancellation(item) || this.isCancellationCompleted(item)) {
      return false;
    }
    if (item.isPendingAddp) {
      return this.canCancelPendingAddp(item);
    }
    return this.canCancelLedgerItem(item);
  }

  private canCancelPendingAddp(item: MarketDataLedgerOrderFormItem): boolean {
    if (!item.isPendingAddp || !item.addpRequestId) {
      return false;
    }
    const status = (item.pendingRequestStatus || '').toLowerCase();
    if (status && status !== 'pending') {
      return false;
    }
    const createdAt = this.parseDate(item.createdAt);
    if (!createdAt) {
      return false;
    }
    const deadline = this.computeCancellationDeadline(createdAt);
    return new Date() <= deadline;
  }

  private canCancelLedgerItem(item: MarketDataLedgerOrderFormItem): boolean {
    if (item.isPendingAddp || !item.uuid) {
      return false;
    }
    const createdAt = this.parseDate(item.createdAt);
    if (!createdAt) {
      return false;
    }
    const deadline = this.computeCancellationDeadline(createdAt);
    return new Date() <= deadline;
  }

  hasPendingCancellation(item: MarketDataLedgerOrderFormItem): boolean {
    const status = (item.cancellationStatus || '').toLowerCase();
    return status === 'pending' || status === 'in_progress';
  }

  isCancellationCompleted(item: MarketDataLedgerOrderFormItem): boolean {
    return (item.cancellationStatus || '').toLowerCase() === 'completed';
  }

  isCreationInProgress(item: MarketDataLedgerOrderFormItem): boolean {
    if (!item.isPendingAddp) {
      return false;
    }
    const status = (item.pendingRequestStatus || '').toLowerCase();
    return status === 'in_progress' || status === 'completed_pending_sync';
  }

  openAddpDetails(item: MarketDataLedgerOrderFormItem): void {
    const requestId = item.addpRequestId;
    if (!requestId || this.loadingAddpDetails) {
      return;
    }

    this.addpDetailsVisible = true;
    this.loadingAddpDetails = true;
    this.addpDetailsError = null;
    this.selectedAddpDetails = null;

    this.orderFormService.getAddpRequestDetails(requestId).subscribe({
      next: details => {
        this.selectedAddpDetails = details;
        this.loadingAddpDetails = false;
      },
      error: () => {
        this.addpDetailsError = 'Unable to load ADDP request details right now.';
        this.loadingAddpDetails = false;
      }
    });
  }

  onAddpDetailsVisibilityChange(visible: boolean): void {
    this.addpDetailsVisible = visible;
    if (!visible) {
      this.loadingAddpDetails = false;
      this.addpDetailsError = null;
      this.selectedAddpDetails = null;
    }
  }

  formatAddpDetailsTitle(details: AddpRequestDetailsResponse | null): string {
    if (!details) {
      return 'ADDP request details';
    }
    const companyName = this.getDetailCompanyName(details);
    const prefix = companyName ? `Extra XCO (ADDP) for ${companyName}` : 'ADDP';
    const pop = this.getDetailPopLabel(details);
    return pop ? `${prefix} · ${pop}` : prefix;
  }

  formatAddpDetailsProducts(details: AddpRequestDetailsResponse | null): string {
    const products = details?.payload?.['items'];
    if (!Array.isArray(products) || products.length === 0) {
      return 'No product selected';
    }
    return products
      .map((entry: any) => entry?.name || entry?.product?.name)
      .filter(Boolean)
      .join(', ');
  }

  getAddpDetailsOriginalXco(details: AddpRequestDetailsResponse | null): string | null {
    const xco = details?.payload?.['xco'];
    if (typeof xco === 'string' && xco.trim().length > 0) {
      return xco.trim();
    }
    return null;
  }

  getAddpDetailsTechnicalPopName(details: AddpRequestDetailsResponse | null): string | null {
    const name = details?.payload?.['connectionPoint']?.['crossConnect']?.['technicalPop']?.['name'];
    if (typeof name === 'string' && name.trim().length > 0) {
      return name.trim();
    }
    return null;
  }

  getAddpDetailsService(details: AddpRequestDetailsResponse | null): string {
    const service = details?.payload?.['service'];
    return typeof service === 'string' && service.trim().length > 0 ? service.trim() : 'ADDP';
  }

  getAddpDetailsDialogTitle(): string {
    return 'Details';
  }

  getAddpDetailsRequesterLine(details: AddpRequestDetailsResponse | null): string | null {
    if (!details?.createdAt) {
      return null;
    }

    const createdAt = new Date(details.createdAt);
    if (Number.isNaN(createdAt.getTime())) {
      return null;
    }

    const formattedDate = createdAt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    const requester = this.formatContactIdentity(details.requester);
    const primaryContact = this.formatContactIdentity(details.primaryContact);

    let line = `ADDP requested on ${formattedDate}`;
    if (requester) {
      line += ` by ${requester}`;
    }
    if (primaryContact) {
      line += ` — Primary Contact: ${primaryContact}`;
    }

    return line;
  }

  private getDetailCompanyName(details: AddpRequestDetailsResponse | null): string | null {
    const name = details?.payload?.['company']?.['name'];
    if (typeof name === 'string' && name.trim().length > 0) {
      return name.trim();
    }
    return null;
  }

  private getDetailPopLabel(details: AddpRequestDetailsResponse | null): string {
    const displayId = details?.payload?.['pop']?.['displayId'] || details?.payload?.['pop']?.['name'] || '';
    const technicalPop = details?.payload?.['connectionPoint']?.['crossConnect']?.['technicalPop']?.['technicalId'] || '';
    if (displayId && technicalPop) {
      return `${displayId} (${technicalPop})`;
    }
    return displayId || technicalPop || 'PoP';
  }

  private formatContactIdentity(contact: { name?: string | null; email?: string | null } | null | undefined): string | null {
    const name = typeof contact?.name === 'string' ? contact.name.trim() : '';
    const email = typeof contact?.email === 'string' ? contact.email.trim() : '';

    if (name && email) {
      return `${name} (${email})`;
    }
    if (email) {
      return email;
    }
    if (name) {
      return name;
    }

    return null;
  }

  private cancelLedgerEntry(item: MarketDataLedgerOrderFormItem): void {
    const cancellationKey = this.getCancellationKey(item);
    if (!cancellationKey) {
      return;
    }
    this.cancellingRequestId = cancellationKey;
    const request$ = item.isPendingAddp
      ? this.orderFormService.cancelAddpRequest(item.addpRequestId as string)
      : this.orderFormService.cancelLedgerItem(item.uuid as string);

    request$.subscribe({
      next: () => {
        this.cancellingRequestId = null;
        this.utils.displayMessage(
          item.isPendingAddp ? 'ADDP request cancelled' : 'Cancellation requested',
          item.isPendingAddp ? '' : 'We will process your cancellation shortly.',
          'success',
          false,
          false,
          { disableCustomClose: true }
        );
        this.loadLedgers(true);
      },
      error: () => {
        this.cancellingRequestId = null;
        this.utils.displayMessage(
          'Unable to cancel',
          'Please try again later.',
          'error',
          false,
          false,
          { disableCustomClose: true }
        );
      }
    });
  }

  getCancellationKey(item: MarketDataLedgerOrderFormItem): string | null {
    if (item.isPendingAddp) {
      return item.addpRequestId ?? null;
    }
    return item.uuid ?? null;
  }

  hasMdmLink(item: MarketDataLedgerOrderFormItem | null | undefined): boolean {
    if (!this.isMckayUser || !item || item.isPendingAddp) {
      return false;
    }
    const uuid = item.uuid;
    if (!uuid || typeof uuid !== 'string') {
      return false;
    }
    return !uuid.startsWith('pending-');
  }

  getMdmLink(item: MarketDataLedgerOrderFormItem | null | undefined): string | null {
    if (!this.hasMdmLink(item) || !item?.uuid) {
      return null;
    }
    return `${this.mdmItemUrl}${encodeURIComponent(item.uuid)}`;
  }

  private computeCancellationDeadline(createdAt: Date): Date {
    const requestDay = this.normalizeDate(createdAt);
    const deadline = new Date(requestDay);
    deadline.setDate(deadline.getDate() + 30);
    deadline.setHours(23, 59, 59, 999);
    return deadline;
  }

  private loadCompanies(): void {
    this.loadingCompanies = true;
    this.companyError = null;
    this.companySelectionService.loadCompanies().subscribe({
      next: companies => {
        this.companies = companies;
        this.companyOptions = companies.map(company => ({
          label: company.name,
          value: company.id
        }));
        this.loadingCompanies = false;
      },
      error: () => {
        this.loadingCompanies = false;
        this.companyError = 'Unable to load companies.';
      }
    });
  }

  private resetLedgerState(): void {
    this.ledgers = [];
    this.ledgerOptions = [];
    this.selectedLedger = null;
    this.selectedLedgerId = null;
    this.error = false;
    this.loading = false;
  }

  private loadLedgers(preserveSelection = false): void {
    if (!this.selectedCompanyId) {
      this.resetLedgerState();
      return;
    }
    const previousSelection = preserveSelection ? this.selectedLedgerId : null;
    this.loading = true;
    this.error = false;
    this.orderFormService.getLedgerOrderForms(this.selectedCompanyId).subscribe({
      next: response => {
        this.ledgers = (response.orderForms ?? []).map(entry => ({
          orderForm: entry.orderForm,
          items: this.sortLedgerItems((entry.items ?? []).map(item => this.enrichLedgerItem(item))),
          commercialPops: this.prepareCommercialPops(entry.commercialPops ?? [])
        }));
        this.ledgerOptions = this.ledgers.map(ledger => ({
          label: `${ledger.orderForm.identificationNumber} - ${ledger.orderForm.name || ledger.orderForm.identificationNumber}`,
          value: ledger.orderForm.identificationNumber
        }));
        if (previousSelection) {
          this.selectedLedger = this.ledgers.find(ledger => ledger.orderForm.identificationNumber === previousSelection) ?? null;
          this.selectedLedgerId = this.selectedLedger ? previousSelection : (this.ledgers[0]?.orderForm?.identificationNumber ?? null);
          if (!this.selectedLedger && this.ledgers.length) {
            this.selectedLedger = this.ledgers[0];
          }
        } else {
          this.selectedLedger = this.ledgers[0] ?? null;
          this.selectedLedgerId = this.ledgers[0]?.orderForm?.identificationNumber ?? null;
        }
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  private buildMdmItemUrlBase(): string {
    const DEFAULT_URL = 'https://hub.infra.mckay-brothers.com/projects/mdm';
    let base = environment.mdmUrl && environment.mdmUrl !== '' ? environment.mdmUrl : DEFAULT_URL;
    base = base.replace(/\/+$/, '');
    if (base.endsWith('/#/item-edit') || base.endsWith('/#/item-edit/')) {
      return base.endsWith('/') ? base : `${base}/`;
    }
    if (base.includes('#')) {
      return base.endsWith('/') ? `${base}item-edit/` : `${base}/item-edit/`;
    }
    return `${base}/#/item-edit/`;
  }
}
